import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const today = () => new Date().toISOString().slice(0, 10);

function asImpactList(value: unknown): Array<{ code?: string; label?: string }> {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function formatImpactLabels(value: unknown): string {
  const labels = asImpactList(value)
    .map((i) => i?.label || i?.code)
    .filter(Boolean);
  return labels.length ? labels.join(', ') : '—';
}

function formatWhen(value: unknown, mode: 'dateTime' | 'time' = 'dateTime'): string {
  if (value == null || value === '') return mode === 'time' ? 'No Record' : '—';
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) return '—';
  return mode === 'time' ? d.toLocaleTimeString() : d.toLocaleString();
}

const ManualTimeAdjustmentsReportPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || searchParams.get('workDate') || today());
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || searchParams.get('workDate') || today());
  const [adjustmentType, setAdjustmentType] = useState('all');
  const [impactLevel, setImpactLevel] = useState('all');
  const [reviewStatus, setReviewStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<any | null>(null);
  const [reviewComment, setReviewComment] = useState('');
  const employeeId = searchParams.get('employeeId') || undefined;
  const workDate = searchParams.get('workDate') || undefined;

  const canReview = (user?.permissions || []).includes('time_adjustments.review');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['manual-time-adjustments', startDate, endDate, adjustmentType, impactLevel, reviewStatus, page, employeeId, workDate],
    queryFn: async () => {
      const response = await apiClient.manualTimeAdjustments.list({
        startDate: workDate || startDate,
        endDate: workDate || endDate,
        adjustmentType,
        impactLevel,
        reviewStatus,
        employeeId,
        page,
        limit: 50,
      });
      return response.data;
    },
  });

  const rows = Array.isArray(data?.data) ? data.data : [];
  const summary = data?.summary;
  const pagination = data?.pagination;

  const handleReview = async (status: 'REVIEWED' | 'APPROVED' | 'QUESTIONED') => {
    if (!selected) return;
    try {
      await apiClient.manualTimeAdjustments.review(selected.id, {
        reviewStatus: status,
        reviewComment,
      });
      toast.success(`Marked ${status.replace(/_/g, ' ').toLowerCase()}`);
      setSelected(null);
      setReviewComment('');
      queryClient.invalidateQueries({ queryKey: ['manual-time-adjustments'] });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Review failed');
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiClient.manualTimeAdjustments.exportCsv({
        startDate: workDate || startDate,
        endDate: workDate || endDate,
        adjustmentType,
        impactLevel,
        reviewStatus,
        employeeId,
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'manual-time-adjustments.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Export failed');
    }
  };

  const summaryChips = useMemo(() => ([
    { label: 'Total', value: summary?.total ?? 0 },
    { label: 'Employees', value: summary?.employeesAffected ?? 0 },
    { label: 'Adds', value: summary?.adds ?? 0 },
    { label: 'Edits', value: summary?.edits ?? 0 },
    { label: 'Deletes', value: summary?.deletes ?? 0 },
    { label: 'High Impact', value: summary?.highImpact ?? 0 },
    { label: 'Pending Review', value: summary?.pendingReview ?? 0 },
  ]), [summary]);

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Manual Time Record Adjustments</Typography>
        <Button variant="outlined" onClick={handleExport}>Export CSV</Button>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="date" label="From" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="date" label="To" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select label="Type" value={adjustmentType} onChange={(e) => { setAdjustmentType(e.target.value); setPage(1); }}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="MANUAL_ADD">Add</MenuItem>
                  <MenuItem value="MANUAL_EDIT">Edit</MenuItem>
                  <MenuItem value="MANUAL_DELETE">Delete</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Impact</InputLabel>
                <Select label="Impact" value={impactLevel} onChange={(e) => { setImpactLevel(e.target.value); setPage(1); }}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="INFORMATIONAL">Informational</MenuItem>
                  <MenuItem value="REVIEW">Review</MenuItem>
                  <MenuItem value="HIGH_IMPACT">High Impact</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Review</InputLabel>
                <Select label="Review" value={reviewStatus} onChange={(e) => { setReviewStatus(e.target.value); setPage(1); }}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="PENDING_REVIEW">Pending</MenuItem>
                  <MenuItem value="REVIEWED">Reviewed</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="QUESTIONED">Questioned</MenuItem>
                  <MenuItem value="NOT_REQUIRED">Not Required</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {summaryChips.map((chip) => (
          <Chip key={chip.label} label={`${chip.label}: ${chip.value}`} color={chip.label === 'High Impact' || chip.label === 'Pending Review' ? 'warning' : 'default'} />
        ))}
      </Stack>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : isError ? (
        <Alert severity="error">{(error as any)?.response?.data?.message || 'Failed to load adjustments'}</Alert>
      ) : (
        <Card>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Changed At</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Work Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Record</TableCell>
                <TableCell>Before</TableCell>
                <TableCell>After</TableCell>
                <TableCell>Impact</TableCell>
                <TableCell>Changed By</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Review</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row: any) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelected(row)}
                >
                  <TableCell>{formatWhen(row.changedAt)}</TableCell>
                  <TableCell>{row.employeeName || '—'}</TableCell>
                  <TableCell>{row.workDate || '—'}</TableCell>
                  <TableCell>{String(row.adjustmentType || '').replace('MANUAL_', '') || '—'}</TableCell>
                  <TableCell>{row.recordType || '—'}</TableCell>
                  <TableCell>{row.before ? formatWhen(row.before, 'time') : 'No Record'}</TableCell>
                  <TableCell>
                    {row.after
                      ? formatWhen(row.after, 'time')
                      : (row.adjustmentType === 'MANUAL_DELETE' ? 'Deleted' : '—')}
                  </TableCell>
                  <TableCell>{formatImpactLabels(row.attendanceImpacts)}</TableCell>
                  <TableCell>{row.changedByName || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.impactLevel || '—'}
                      color={row.impactLevel === 'HIGH_IMPACT' ? 'error' : row.impactLevel === 'REVIEW' ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{String(row.reviewStatus || '').replace(/_/g, ' ') || '—'}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No manual adjustments for the selected filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {pagination && pagination.totalPages > 1 && (
            <Box display="flex" justifyContent="center" py={2}>
              <Pagination page={page} count={pagination.totalPages} onChange={(_, p) => setPage(p)} />
            </Box>
          )}
        </Card>
      )}

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Manual Adjustment Detail</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={1.25}>
              <Typography><strong>Type:</strong> {selected.adjustmentType}</Typography>
              <Typography><strong>Severity:</strong> {selected.impactLevel}</Typography>
              <Typography><strong>Review:</strong> {selected.reviewStatus}</Typography>
              <Typography><strong>Employee:</strong> {selected.employeeName}</Typography>
              <Typography><strong>Work Date:</strong> {selected.workDate}</Typography>
              <Typography><strong>Record:</strong> {selected.recordType}</Typography>
              <Typography><strong>Original:</strong> {selected.before ? formatWhen(selected.before) : 'No Record'}</Typography>
              <Typography><strong>Changed To:</strong> {selected.after ? formatWhen(selected.after) : (selected.adjustmentType === 'MANUAL_DELETE' ? 'Record Deleted' : '—')}</Typography>
              <Typography><strong>Impact:</strong> {formatImpactLabels(selected.attendanceImpacts)}</Typography>
              <Typography><strong>Changed By:</strong> {selected.changedByName || '—'} ({selected.changedByRole || '—'})</Typography>
              <Typography><strong>Changed At:</strong> {formatWhen(selected.changedAt)}</Typography>
              <Typography><strong>Reason:</strong> {selected.reasonCode}</Typography>
              <Typography><strong>Comment:</strong> {selected.reasonComment || '—'}</Typography>
              {selected.auditLogId && <Typography variant="caption">Audit Event: {selected.auditLogId}</Typography>}
              {canReview && selected.reviewStatus === 'PENDING_REVIEW' && (
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Review Comment"
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
          {canReview && selected?.reviewStatus === 'PENDING_REVIEW' && (
            <>
              <Button onClick={() => handleReview('QUESTIONED')} color="warning">Question</Button>
              <Button onClick={() => handleReview('REVIEWED')}>Mark Reviewed</Button>
              <Button onClick={() => handleReview('APPROVED')} variant="contained">Approve</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManualTimeAdjustmentsReportPage;
